<script setup lang="ts">
import { computed, ref } from "vue";
import { TabsContent, TabsList, TabsRoot, TabsTrigger } from "reka-ui";

import { PANEL_SIZE } from "./RecordView/constants";

import { useCaseRouteParams } from "@/hooks/use-case-route-params";

import type { Metadata } from "@/models/Metadata";

withDefaults(defineProps<{
  parentFileReference: string | undefined;
  attachments: Record<"filteredFiles" | "unfilteredFiles", Record<"siblings" | "children", Metadata[]>>;
}>(), {  })

const routeParams = useCaseRouteParams();

const id = Math.random().toString(36);
const useAppliedFilters = ref(true);
const activeGroup = computed(() => (useAppliedFilters.value ? __props.attachments.filteredFiles : __props.attachments.unfilteredFiles));

function setUseAppliedFilters(checked: boolean) {
  useAppliedFilters.value = checked;
}
</script>
<template>
  <div :style="{ width: PANEL_SIZE + 'px' }" class="attachments-panel tw-flex tw-h-full tw-flex-col tw-gap-2 tw-shadow">
    <button
      v-if="parentFileReference != null"
      type="button"
      class="tw-btn-lg-gray tw-grid tw-size-full tw-place-items-center"
      @click="
        () =>
          $router.push({
            name: 'casefile',
            params: { brand: routeParams.brand, casename: routeParams.casename, casefile: parentFileReference },
          })
      "
    >
      Back To Parent
    </button>
    <TabsRoot :default-value="activeGroup.children.length > 0 ? 'children' : 'siblings'" class="tw-space-y-1">
      <div class="tw-flex tw-min-h-0 tw-shrink-0 tw-gap-2 tw-py-1">
        <input
          :id="`useAppliesFilters-${id}`"
          type="checkbox"
          class="tw-shrink-0"
          :checked="useAppliedFilters"
          @change="setUseAppliedFilters(($event.target as HTMLInputElement).checked)"
        />
        <label :for="`useAppliesFilters-${id}`" class="tw-flex-1 tw-cursor-pointer tw-select-none tw-text-sm">
          Use applied groups and filters
        </label>
      </div>
      <TabsList class="tw-flex tw-rounded-md tw-bg-white tw-p-0.5">
        <TabsTrigger
          v-for="(files, groupKey) in activeGroup"
          :key="groupKey"
          :value="groupKey"
          class="tw-flex-1 tw-rounded tw-py-0.5 data-[state=active]:tw-bg-gray-300"
        >
          {{ groupKey === "children" ? "Children" : "Siblings" }} ({{ files.length }})
        </TabsTrigger>
      </TabsList>
      <TabsContent v-for="(files, groupKey) in activeGroup" :key="groupKey" :value="groupKey" class="tw-space-y-2">
        <div
          v-if="files.length > 0"
          class="tw-grid tw-grid-cols-[max-content_minmax(0,1fr)] tw-overflow-hidden tw-rounded-sm"
        >
          <router-link
            v-for="(file, index) in files"
            :key="file.fileReference"
            class="tw-group tw-col-span-full tw-grid tw-grid-cols-subgrid tw-gap-1 tw-py-1 tw-pl-1 hover:tw-bg-white"
            :to="{
              name: 'casefile',
              params: { brand: routeParams.brand, casename: routeParams.casename, casefile: file.fileReference },
            }"
          >
            <span>{{ index + 1 }}.</span>
            <p>{{ file.displayName }}</p>
          </router-link>
        </div>
        <div v-else class="tw-grid tw-w-full tw-place-items-center tw-px-2 tw-py-1">No results found</div>
      </TabsContent>
    </TabsRoot>
  </div>
</template>
<style lang="scss" scoped>
input {
  padding: 4px;
}
.attachments-panel {
  padding: 12px 8px 2px 8px;
}

.results-header {
  background: #2b1b4f;
  color: white;
  font-size: 16px;
  font-weight: 500;
  padding: 2px 4px;
}
.results {
  // background: #f2f3f6;
  overflow: auto;
  min-height: 0;

  .result {
    padding: 8px;
    cursor: pointer;
    display: block;

    &.odd {
      background: white;
    }
  }
}
</style>
